// The calls the new self-service screens (P12) make, all through the one axios instance the rest of
// the app uses (utils/authUtils: it attaches the JWT and refreshes it). Kept in one module so the
// screens stay presentational and the tests can mock this module instead of axios.
//
// Route shapes are the controllers of heartscms: me.MeController, me.MeChangeSetController,
// me.MeInvitationController, me.MeRegistrationController, me.MeTalkController and signup.
// SignupController / signup.InvitationLinkController. The /api/me/** area answers only to a
// ROLE_MEMBER token (authority.MeRoutes) and signup's POSTs are open since the switch (P15) wired
// signup.PublicRoutes into SecurityConfig.
import api from '../../utils/authUtils';

// ---------------------------------------------------------------- the member's own profile (me.MeController)

// {account: {id, email, emailVerified}, personId, profile, roles, changeSetsWaiting}
export const fetchMe = () => api.get('/api/me').then((r) => r.data);

// Only the keys present change; one SELF change set, applied in the same transaction. Answers as GET.
export const updateProfile = (profile) => api.put('/api/me/profile', profile).then((r) => r.data);

// The profile edit form's photo upload: checked and stored only, {photoUrl} answered - the form puts
// the URL into its photoUrl field and its save (updateProfile) applies it, so Cancel undoes it.
export const storeProfilePhotoFile = (file) => {
  const body = new FormData();
  body.append('file', file);
  return api.post('/api/me/profile/photo-file', body).then((r) => r.data.photoUrl);
};

// A new "confirm your email" link to the member's own address (me.MeVerification, throttled to one
// mail per 2 minutes). The answer is the same whether or not a mail went out, so the page shows one
// neutral sentence and never claims a mail was sent.
export const resendVerificationEmail = () => api.post('/api/me/email-verification').then((r) => r.data);

// ---------------------------------------------------------------- recommended studies (me.MeStudiesController)

// {austrian, top, new, hasMore} - two studies each from the separate HEARTPORT EXPERT project, chosen
// by the person's own interests: [{pmid, title, authors, journalName, url, group, reason,
// matchedInterest, textLanguage, isNew}]. `matchedInterest` is null when general cardiology filled
// the slot (reason GENERAL); `textLanguage` is the language of title and German summary (German when
// Expert has no summary in the page language); `authors` are "Last First" strings, never null.
export const fetchStudies = (language) => api
  .get('/api/me/studies', { params: { lang: language } }).then((r) => r.data);

// One "Show more" page of one row: group austrian|top|new, offset the index of the first study.
// Answers {studies, hasMore}, at most two studies.
export const fetchStudiesPage = (group, offset, language) => api
  .get(`/api/me/studies/${encodeURIComponent(group)}`, { params: { lang: language, offset } }).then((r) => r.data);

// {hasProfile, url, suggestions, confirmed, rejected} - whether the person has a public profile and its
// page (/people/{id}), and their co-authored Austrian studies: HEARTPORT EXPERT's suggestions by name
// and the studies they (or the organizers) confirmed or rejected (me.MePublicProfileController).
export const fetchPublicProfile = () => api.get('/api/me/public-profile').then((r) => r.data);

// decision: 'CONFIRMED', 'REJECTED' or null (removes the decision).
export const decideStudy = (pmid, decision) => api
  .put(`/api/me/public-profile/studies/${encodeURIComponent(pmid)}`, { decision }).then((r) => r.data);

// ---------------------------------------------------------------- proposed changes (me.MeChangeSetController)

// The person's SENT change sets: [{id, origin, status, invitationEmailId, createdAt,
// fields: [{id, field, current, proposed, confidence, sources, state: WAITING|APPLIED|REJECTED}]}]
export const fetchChangeSets = () => api.get('/api/me/change-sets').then((r) => r.data);

// several fields at once: {institution: 'ACCEPT'|'REJECT', ...}
export const decideChangeSet = (id, decisions) => api
  .post(`/api/me/change-sets/${id}/decisions`, { decisions }).then((r) => r.data);

export const acceptField = (id) => api.post(`/api/me/field-changes/${id}/accept`).then((r) => r.data);
export const rejectField = (id) => api.post(`/api/me/field-changes/${id}/reject`).then((r) => r.data);

// ---------------------------------------------------------------- invitations (me.MeInvitationController)

// [{eventId, event: {id, slug, name, nameEn, dateTime, location, imageUrl, ...},
//   spots: [{invitationId, assignmentId, entryId, role, entryTitle, blockTitle, date, time, blockTime,
//            state: OPEN|CONFIRMED|DECLINED|WITHDRAWN|CANCELLED, askedAt, decidedAt, noticeSentAt, removed}]}]
export const fetchInvitations = () => api.get('/api/me/invitations').then((r) => r.data);

export const acceptAssignment = (id) => api.post(`/api/me/assignments/${id}/accept`).then((r) => r.data);

export const declineAssignment = (id, { replacementName, replacementEmail } = {}) => api
  .post(`/api/me/assignments/${id}/decline`, { replacementName: replacementName || null, replacementEmail: replacementEmail || null })
  .then((r) => r.data);

// ---------------------------------------------------------------- registration and history (me.MeRegistrationController)

// {status: CONFIRMED|PENDING, eventId, eventName, eventNameEn}; 409 already registered,
// 403 {error: 'EMAIL_NOT_VERIFIED'}. No P12 screen registers yet (today's event page does that
// through its own modal); the route is wrapped here for the switch, which points that button at it.
export const registerForEvent = (eventId) => api.post(`/api/me/events/${eventId}/registration`).then((r) => r.data);

// the person's registrations and submissions, newest first
export const fetchHistory = () => api.get('/api/me/history').then((r) => r.data);

// ---------------------------------------------------------------- talks and materials (me.MeTalkController, speaker-only)

// {consentTextVersion, talks: [{eventId, eventName, eventNameEn, eventDateTime, eventStartsOn,
//   eventEndsOn, eventLocation, eventImageUrl, programStatus, entryId, titleEn, titleDe, asksForSlides,
//   assignmentId, consent, consentAt, presentsWithoutSlides, titleSuggestion,
//   decks: [{id, position, versionId, fileName, sizeBytes, uploadedAt, uploadedByName,
//            presenterAssignmentIds, published}],
//   order: {needsConfirmation, confirmedAt, confirmedByName},
//   coSpeakers: [{assignmentId, personId, name, confirmed, email}],
//   readiness: {me: COVERED|WITHOUT_SLIDES|MISSING, missingAssignmentIds, orphanDeckIds}}]} - one
// row per CONFIRMED SPEAKER spot of the person (MaterialService.talksOf); `versionId` is the current
// version, which every download route takes.
export const fetchTalks = () => api.get('/api/me/talks').then((r) => r.data);

// The talk's files, one call per write of me.MeTalkController; every answer is the talk's row with
// its files, presenters and order as they now stand.
//
// `position` is the 1-based place in the order and is required once the talk has files (the backend
// refuses a missing one); `presenterAssignmentIds` are the confirmed speaker spots presenting the
// new file (at least one).
export const addDeck = (entryId, file, { position = null, presenterAssignmentIds = [] } = {}) => {
  const body = new FormData();
  body.append('file', file);
  if (position != null) body.append('position', String(position));
  presenterAssignmentIds.forEach((id) => body.append('presenterAssignmentIds', String(id)));
  return api.post(`/api/me/talks/${entryId}/decks`, body).then((r) => r.data);
};

// A new version of one file: the previous one is kept for staff and the file is unpublished.
export const addDeckVersion = (entryId, deckId, file) => {
  const body = new FormData();
  body.append('file', file);
  return api.post(`/api/me/talks/${entryId}/decks/${deckId}/file`, body).then((r) => r.data);
};

// The file with all its versions; its stored bytes are deleted after the commit.
export const removeDeck = (entryId, deckId) => api.delete(`/api/me/talks/${entryId}/decks/${deckId}`).then((r) => r.data);

// The complete list of the talk's deck ids in their new order: exactly the current set, or a 409.
export const reorderDecks = (entryId, deckIds) => api
  .put(`/api/me/talks/${entryId}/decks/order`, { deckIds }).then((r) => r.data);

// The complete set of spots presenting one file (at least one, else 400).
export const setDeckPresenters = (entryId, deckId, assignmentIds) => api
  .put(`/api/me/talks/${entryId}/decks/${deckId}/presenters`, { assignmentIds }).then((r) => r.data);

// "This is the order we present in": the deck ids in the order being confirmed (two or more, exactly
// the current order, else 409).
export const confirmDeckOrder = (entryId, deckIds) => api
  .post(`/api/me/talks/${entryId}/order-confirmation`, { deckIds }).then((r) => r.data);

// The speaker's own "I present without slides"; the backend removes them from the files they present.
export const setPresentsWithoutSlides = (assignmentId, presentsWithoutSlides) => api
  .put(`/api/me/assignments/${assignmentId}/without-slides`, { presentsWithoutSlides }).then((r) => r.data);

export const suggestTalkTitle = (entryId, title) => api
  .put(`/api/me/talks/${entryId}/title-suggestion`, { title }).then((r) => r.data);

export const setMaterialsConsent = (assignmentId, consent) => api
  .put(`/api/me/assignments/${assignmentId}/materials-consent`, { consent }).then((r) => r.data);

// The file is behind the bearer token, so it cannot be a plain <a href>: fetch it and hand the blob
// to the browser as a download. The route takes the current VERSION id.
export const downloadDeck = async (versionId, fileName) => {
  const { data } = await api.get(`/api/me/materials/${versionId}/file`, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'slides.pptx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------- signup (signup.SignupController, public)

// always the same answer
export const startSignup = (email, eventId) => api
  .post('/api/public/signup/start', { email, eventId: eventId ?? null }).then((r) => r.data);

// {status: ACCOUNT_EXISTS|PERSON_FOUND|NEW, email, prefill?}; 400 for an invalid or expired link
export const signupStatus = (token) => api
  .get('/api/public/signup/status', { params: { token } }).then((r) => r.data);

// both answer {token, refreshToken, role, roles, registeredEventId?, registeredEventName?,
// registeredEventNameEn?, registrationStatus?}
export const claimSignup = (token, password, profile) => api
  .post('/api/public/signup/claim', { token, password, profile }).then((r) => r.data);

export const freshSignup = (token, password, profile) => api
  .post('/api/public/signup/fresh', { token, password, profile }).then((r) => r.data);

// ---------------------------------------------------------------- invite landing and resend (signup.InvitationLinkController, public)

// {state: VALID, needsPassword, loginEmail, title, firstName, lastName, eventId} or {state: NO_LONGER_WORKS}
export const checkInviteLink = (token) => api
  .get(`/api/public/invitation-links/${encodeURIComponent(token)}`).then((r) => r.data);

// {token, refreshToken, role, roles}; 410 for every link that no longer works, 409 when the person has a login
export const activateInviteLink = (token, password) => api
  .post(`/api/public/invitation-links/${encodeURIComponent(token)}/activate`, { password }).then((r) => r.data);

// "forgot password" before activation: always the same answer
export const resendInvitationLink = (email) => api
  .post('/api/public/invitation-links/resend', { email }).then((r) => r.data);
